import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Typography,
  Box,
  Card,
  CardContent,
  CardActions,
  Button,
  CircularProgress,
  Chip,
  Alert,
  AlertTitle,
  AppBar,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Badge,
  Avatar,
  Tab,
  Tabs,
  Grid,
  Divider,
  LinearProgress,
  Modal,
  Paper,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Snackbar,
  Backdrop,
  useTheme,
  useMediaQuery,
  Collapse,
  SwipeableDrawer
} from '@mui/material';
import {
  Menu as MenuIcon,
  AccountCircle,
  Logout as LogoutIcon,
  Edit as EditIcon,
  CalendarMonth as CalendarIcon,
  StarBorder as StarIcon,
  Campaign as CampaignIcon,
  Assignment as AssignmentIcon,
  Payment as PaymentIcon,
  Notifications as NotificationsIcon,
  School as SchoolIcon,
  Refresh as RefreshIcon,
  Description as ArticleIcon,
  Add as AddIcon,
  ChevronRight as ChevronRightIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
  Event as EventIcon,
  Recommend as RecommendIcon,
  Assessment as AssessmentIcon,
  Close as CloseIcon,
  AssignmentInd as AssignmentIndIcon,
  Chat as ChatIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Download as DownloadIcon,
  Send as SendIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MoreVert as MoreVertIcon,
  LibraryBooks as LibraryBooksIcon,
  OpenInNew as OpenInNewIcon,
  Inventory2 as Inventory2Icon
} from '@mui/icons-material';

import Footer from '../../components/Footer/Footer';
import { useAuth } from '../../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { useNotificacao } from '../../contexts/NotificacaoContext';
import api from '../../services/api';
import CentralNotificacoes from '../../components/Notificacoes/CentralNotificacoes';
import PlanosPagamentoEstudante from '../Estudantes/PlanosPagamentoEstudante';
import RecomendacoesOverview from '../../components/Recomendacoes/RecomendacoesOverview';

import { listarProjetos } from '../../services/projetoService';
import { 
  listarMonografias, 
  voltarMonografiaParaRevisao, 
  criarMonografia,
  resubmeterMonografiaAposCorrecoes,
  submeterParaDirecaoCientifica,
  getStatusDisplayName,
  getStatusColor,
  obterHistoricoMonografia,
  obterDocumentoCorrigido,
  obterEstatisticasVersionamento,
  obterVersaesDocumentos,
  getAvailableActions
} from '../../services/monografiaService';
import { listarDefesas } from '../../services/defesaService';
import { listarAvaliacoes } from '../../services/avaliacaoService';
import { listarArquivosDigitais } from '../../services/arquivoDigitalService';
import ChatDialog from '../../components/ChatDialog';
import { getDocumentUrl } from '../../utils/apiUtils';

import "slick-carousel/slick/slick.css"; 
import "slick-carousel/slick/slick-theme.css";
import Slider from "react-slick";

// COMPONENTE DE NOTIFICAÇÃO CUSTOMIZADO MOBILE
const NotificationSnackbar = ({ open, message, severity, onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const getIcon = () => {
    switch (severity) {
      case 'success': return <CheckCircleIcon />;
      case 'error': return <ErrorIcon />;
      case 'warning': return <WarningIcon />;
      case 'info': return <InfoIcon />;
      default: return <InfoIcon />;
    }
  };

  return (
    <Snackbar
      open={open}
      autoHideDuration={isMobile ? 4000 : 8000}
      onClose={onClose}
      anchorOrigin={{ 
        vertical: isMobile ? 'top' : 'top', 
        horizontal: isMobile ? 'center' : 'right' 
      }}
      sx={{
        '& .MuiSnackbar-root': {
          top: isMobile ? '80px !important' : '24px !important'
        }
      }}
    >
      <Alert 
        onClose={onClose} 
        severity={severity} 
        sx={{ 
          width: '100%',
          minWidth: isMobile ? '280px' : '350px',
          maxWidth: isMobile ? '90vw' : '400px',
          fontSize: isMobile ? '0.875rem' : '14px',
          '& .MuiAlert-icon': {
            fontSize: isMobile ? '18px' : '20px'
          }
        }}
        icon={getIcon()}
      >
        {message}
      </Alert>
    </Snackbar>
  );
};

NotificationSnackbar.propTypes = {
  open: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
  severity: PropTypes.oneOf(['success', 'error', 'warning', 'info']).isRequired,
  onClose: PropTypes.func.isRequired
};

// Componente TabPanel Mobile-Friendly
const TabPanel = ({ children, value, index, ...other }) => {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && (
        <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
          {children}
        </Box>
      )}
    </div>
  );
};

TabPanel.propTypes = {
  children: PropTypes.node.isRequired,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

// COMPONENTE CARD EXPANSÍVEL PARA MOBILE
const ExpandableCard = ({ children, title, subtitle, actions, expanded: defaultExpanded = false, statusChip }) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  if (!isMobile) {
    return (
      <Card sx={{ mb: 2, '&:hover': { boxShadow: 4, transform: 'translateY(-1px)', transition: 'all 0.2s' } }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              {title && <Typography variant="h6" gutterBottom>{title}</Typography>}
              {subtitle && <Typography variant="body2" color="text.secondary" gutterBottom>{subtitle}</Typography>}
            </Box>
            {statusChip}
          </Box>
          {children}
        </CardContent>
        {actions && (
          <CardActions sx={{ flexWrap: 'wrap', gap: 1 }}>
            {actions}
          </CardActions>
        )}
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {title && (
              <Typography 
                variant="subtitle1" 
                sx={{ 
                  fontSize: '1rem',
                  fontWeight: 'bold',
                  lineHeight: 1.2,
                  mb: subtitle ? 0.5 : 1
                }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ fontSize: '0.875rem', mb: 1 }}
              >
                {subtitle}
              </Typography>
            )}
            {statusChip && <Box sx={{ mb: 1 }}>{statusChip}</Box>}
          </Box>
          <IconButton 
            onClick={() => setExpanded(!expanded)}
            size="small"
            sx={{ ml: 1 }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </CardContent>
      
      <Collapse in={expanded}>
        <CardContent sx={{ pt: 0 }}>
          {children}
        </CardContent>
        {actions && (
          <CardActions sx={{ p: 2, pt: 0 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: '100%' }}>
              {actions}
            </Box>
          </CardActions>
        )}
      </Collapse>
    </Card>
  );
};

ExpandableCard.propTypes = {
  children: PropTypes.node.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  actions: PropTypes.node,
  expanded: PropTypes.bool,
  statusChip: PropTypes.node
};

// Constante para paginação
const ITEMS_PER_PAGE = 6;

// Configurações do carrossel mobile-friendly
const getCarouselSettings = (isMobile) => ({
  dots: true,
  infinite: true,
  speed: 500,
  slidesToShow: isMobile ? 1 : 3,
  slidesToScroll: 1,
  autoplay: !isMobile,
  autoplaySpeed: 7000,
  pauseOnHover: true,
  swipeToSlide: true,
  touchThreshold: 10,
  arrows: !isMobile,
  responsive: [
    {
      breakpoint: 1024,
      settings: {
        slidesToShow: 2,
        slidesToScroll: 1,
        infinite: true,
        dots: true,
        arrows: false
      }
    },
    {
      breakpoint: 600,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
        initialSlide: 1,
        infinite: true,
        dots: true,
        arrows: false
      }
    }
  ]
});

const EstudanteDashboardPage = () => {
  // Hooks de tema e responsividade
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  // Estados para controle de tabs e dados do dashboard
  const [value, setValue] = useState(0);
  const [dashboardData, setDashboardData] = useState({
    estudante: null,
    campanhasDisponiveis: [],
    preInscricoesRecentes: [],
    proximosPrazos: [],
    pagamentos: [],
    proposta: null
  });
  
  // Estados de controle
  const [loading, setLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pageNumbers, setPageNumbers] = useState({ 0: 1, 1: 1, 2: 1, 3: 1, 4: 1, 5: 1, 6: 1 });
  const [totalPages, setTotalPages] = useState({ 0: 1, 1: 1, 2: 1, 3: 1, 4: 1, 5: 1, 6: 1 });
  const [tccTab, setTccTab] = useState(0);

  // Estados para recomendações
  const [recomendacoes, setRecomendacoes] = useState([]);
  const [perfilAcademico, setPerfilAcademico] = useState(null);
  const [recomendacoesError, setRecomendacoesError] = useState(null);

  // Estados para propostas, projetos, etc.
  const [propostas, setPropostas] = useState([]);
  const [loadingPropostas, setLoadingPropostas] = useState(true);
  const [errorPropostas, setErrorPropostas] = useState(null);

  const [projetos, setProjetos] = useState([]);
  const [loadingProjetos, setLoadingProjetos] = useState(true);
  const [errorProjetos, setErrorProjetos] = useState(null);

  const [monografias, setMonografias] = useState([]);
  const [loadingMonografias, setLoadingMonografias] = useState(true);
  const [errorMonografias, setErrorMonografias] = useState(null);

  const [arquivoDigital, setArquivoDigital] = useState(null);

  const [defesas, setDefesas] = useState([]);
  const [loadingDefesas, setLoadingDefesas] = useState(true);
  const [errorDefesas, setErrorDefesas] = useState(null);

  const [avaliacoes, setAvaliacoes] = useState([]);
  const [loadingAvaliacoes, setLoadingAvaliacoes] = useState(true);
  const [errorAvaliacoes, setErrorAvaliacoes] = useState(null);

  // Estados para o chat
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [selectedChatItem, setSelectedChatItem] = useState(null);
  const [chatMessage, setChatMessage] = useState('');
  const [chatHistory, setChatHistory] = useState([]);
  const [loadingChat, setLoadingChat] = useState(false);

  // Estados para Modal e Alert
  const [showRecomendacoesModal, setShowRecomendacoesModal] = useState(true);
  const [showRecomendacoesAlert, setShowRecomendacoesAlert] = useState(true);
  const [neverShowModal, setNeverShowModal] = useState(false);
  
  // Estado para armazenar as abas disponíveis
  const [availableTabs, setAvailableTabs] = useState([]);
  const [loadingTabs, setLoadingTabs] = useState(true);

  // Estados para notificações e loading
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  const [submittingAction, setSubmittingAction] = useState(false);

  // Estados para histórico
  const [historicoDialogOpen, setHistoricoDialogOpen] = useState(false);
  const [versaoDialogOpen, setVersaoDialogOpen] = useState(false);
  const [historicoData, setHistoricoData] = useState(null);
  const [versaoData, setVersaoData] = useState([]);
  const [selectedMonografiaHistorico, setSelectedMonografiaHistorico] = useState(null);

  // Estados para documentos corrigidos
  const [documentosCorrigidos, setDocumentosCorrigidos] = useState({});
  const [loadingDownload, setLoadingDownload] = useState(false);

  // Hooks
  const { user, logout, checkModuleAccess } = useAuth();
  const { naoLidas, loading: notificacoesLoading, fetchNotificacoes } = useNotificacao();
  const navigate = useNavigate();

  // FUNÇÃO PARA MOSTRAR NOTIFICAÇÕES
  const showNotification = (message, severity = 'info') => {
    setNotification({
      open: true,
      message,
      severity
    });
  };

  // FUNÇÃO PARA FECHAR NOTIFICAÇÕES
  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  // FUNÇÃO MELHORADA PARA TRATAMENTO DE ERROS DE API
  const handleApiError = (error, operacao = 'operação') => {
    console.error(`Erro na ${operacao}:`, error);
    
    let mensagem = '';
    
    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;
      
      switch (status) {
        case 400:
          if (data.message) {
            if (data.message.includes('arquivo') || data.message.includes('PDF') || data.message.includes('Word')) {
              mensagem = `Erro no arquivo: ${data.message}`;
            } else if (data.message.includes('tamanho')) {
              mensagem = `Arquivo muito grande! Máximo: 10MB`;
            } else {
              mensagem = `${data.message}`;
            }
          } else {
            mensagem = `Dados inválidos para ${operacao}. Verifique os campos.`;
          }
          break;
        case 401:
          mensagem = `Sessão expirada. Faça login novamente.`;
          break;
        case 403:
          mensagem = `Sem permissão para ${operacao}.`;
          break;
        case 404:
          mensagem = `Recurso não encontrado.`;
          break;
        case 413:
          mensagem = `Arquivo muito grande! Máximo: 10MB`;
          break;
        case 422:
          if (data.errors && Array.isArray(data.errors)) {
            mensagem = `Erros de validação:\n${data.errors.map(err => `• ${err.message || err}`).join('\n')}`;
          } else {
            mensagem = `Dados inválidos. Verifique as informações.`;
          }
          break;
        case 500:
          mensagem = `Erro interno do servidor. Tente novamente.`;
          break;
        default:
          mensagem = `Erro inesperado. Código: ${status}`;
      }
    } else if (error.request) {
      mensagem = `Erro de conexão. Verifique sua internet.`;
    } else {
      mensagem = `Erro inesperado: ${error.message}`;
    }
    
    showNotification(mensagem, 'error');
    return mensagem;
  };

  // ============================================
  // FUNÇÕES PARA HISTÓRICO E DOCUMENTOS CORRIGIDOS
  // ============================================

  const handleAbrirHistorico = async (monografia) => {
    try {
      setSelectedMonografiaHistorico(monografia);
      showNotification('Carregando histórico completo...', 'info');
      
      const response = await obterHistoricoMonografia(monografia.id);
      setHistoricoData(response.data);
      setHistoricoDialogOpen(true);
      
      showNotification('Histórico carregado com sucesso!', 'success');
    } catch (error) {
      handleApiError(error, 'carregamento do histórico');
    }
  };

  const handleAbrirVersoes = async (monografia) => {
    try {
      setSelectedMonografiaHistorico(monografia);
      showNotification('Carregando versões do documento...', 'info');
      
      const response = await obterVersaesDocumentos(monografia.id);
      setVersaoData(response.data);
      setVersaoDialogOpen(true);
      
      showNotification('Versões carregadas!', 'success');
    } catch (error) {
      handleApiError(error, 'carregamento das versões');
    }
  };

  const extrairObservacoesLimpas = (observacoes) => {
    if (!observacoes) return '';
    
    let observacoesLimpas = observacoes.replace(/\[DOCUMENTO_CORRIGIDO:.*?\]/g, '');
    observacoesLimpas = observacoesLimpas.replace(/\n\s*\n/g, '\n').trim();
    
    return observacoesLimpas;
  };

  const extrairNomeDocumentoCorrigido = (observacoes) => {
    if (!observacoes) return null;
    
    const match = observacoes.match(/\[DOCUMENTO_CORRIGIDO:\s*([^\]]+)\]/);
    return match ? match[1].trim() : null;
  };

  const temDocumentoCorrigido = (observacoes) => {
    if (!observacoes) return false;
    return observacoes.includes('[DOCUMENTO_CORRIGIDO:');
  };

  const verificarPrazosVencidos = (prazo) => {
    if (!prazo) return false;
    
    const agora = new Date();
    const dataPrazo = new Date(prazo);
    
    agora.setHours(0, 0, 0, 0);
    dataPrazo.setHours(0, 0, 0, 0);
    
    return dataPrazo < agora;
  };

  const processarObservacoesSupervisor = (observacoes) => {
    if (!observacoes) {
      return { 
        observacoesLimpas: '', 
        temDocumento: false, 
        nomeArquivo: null 
      };
    }
    
    return {
      observacoesLimpas: extrairObservacoesLimpas(observacoes),
      temDocumento: temDocumentoCorrigido(observacoes),
      nomeArquivo: extrairNomeDocumentoCorrigido(observacoes)
    };
  };

  const verificarDocumentosCorrigidos = async (monografias) => {
    try {
      const documentosMap = {};
      
      for (const monografia of monografias) {
        if (temDocumentoCorrigido(monografia.observacoes_supervisor)) {
          try {
            const response = await obterDocumentoCorrigido(monografia.id);
            documentosMap[monografia.id] = {
              temDocumento: true,
              urlDownload: response.data.url_download,
              nomeArquivo: extrairNomeDocumentoCorrigido(monografia.observacoes_supervisor)
            };
          } catch (error) {
            console.warn(`Documento corrigido não encontrado para monografia ${monografia.id}:`, error);
            documentosMap[monografia.id] = { 
              temDocumento: false,
              erro: error.response?.status === 404 ? 'Documento não encontrado' : 'Erro ao carregar'
            };
          }
        } else {
          documentosMap[monografia.id] = { temDocumento: false };
        }
      }
      
      setDocumentosCorrigidos(documentosMap);
      
      const totalDocumentos = Object.values(documentosMap).filter(doc => doc.temDocumento).length;
      if (totalDocumentos > 0) {
        showNotification(`${totalDocumentos} documento(s) corrigido(s) encontrado(s)!`, 'info');
      }
      
    } catch (error) {
      console.error('Erro ao verificar documentos corrigidos:', error);
      handleApiError(error, 'verificação de documentos corrigidos');
    }
  };

  const downloadDocumentoCorrigido = async (monografiaId) => {
    try {
      setLoadingDownload(true);
      showNotification('Iniciando download do documento corrigido...', 'info');

      const response = await obterDocumentoCorrigido(monografiaId);
      
      if (!response.data.url_download) {
        showNotification('URL de download não encontrada.', 'error');
        return;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Sessão expirada. Faça login novamente.', 'error');
        return;
      }

      const downloadResponse = await fetch(response.data.url_download, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/octet-stream'
        }
      });

      if (downloadResponse.status === 401) {
        showNotification('Sessão expirada. Faça login novamente.', 'error');
        return;
      }

      if (downloadResponse.status === 404) {
        showNotification('Documento corrigido não encontrado no servidor.', 'error');
        return;
      }

      if (!downloadResponse.ok) {
        throw new Error(`Erro HTTP: ${downloadResponse.status} - ${downloadResponse.statusText}`);
      }

      const blob = await downloadResponse.blob();

      if (blob.size === 0) {
        throw new Error('Arquivo vazio recebido do servidor.');
      }

      const monografia = monografias.find(m => m.id === monografiaId);
      const nomeArquivoOriginal = extrairNomeDocumentoCorrigido(monografia?.observacoes_supervisor);
      
      let nomeArquivo = nomeArquivoOriginal || `documento-corrigido-${monografiaId}`;
      
      if (!nomeArquivo.includes('.')) {
        if (blob.type.includes('pdf')) {
          nomeArquivo += '.pdf';
        } else if (blob.type.includes('word') || blob.type.includes('officedocument')) {
          nomeArquivo += '.docx';
        } else {
          nomeArquivo += '.pdf';
        }
      }

      const objectUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = objectUrl;
      downloadLink.download = nomeArquivo;
      downloadLink.style.display = 'none';
      
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      setTimeout(() => {
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(objectUrl);
      }, 100);

      showNotification(`Download concluído: ${nomeArquivo}`, 'success');

    } catch (error) {
      console.error('Erro ao baixar documento corrigido:', error);
      
      let mensagemErro = 'Erro ao baixar documento corrigido';
      
      if (error.message.includes('404')) {
        mensagemErro = 'Documento corrigido não encontrado';
      } else if (error.message.includes('401')) {
        mensagemErro = 'Sessão expirada. Faça login novamente';
      } else if (error.message.includes('403')) {
        mensagemErro = 'Você não tem permissão para baixar este documento';
      } else if (error.message.includes('network') || error.message.includes('fetch')) {
        mensagemErro = 'Erro de conexão. Verifique sua internet';
      }
      
      showNotification(mensagemErro, 'error');
    } finally {
      setLoadingDownload(false);
    }
  };

  // COMPONENTE SEÇÃO CORREÇÕES SOLICITADAS MOBILE
  const SecaoCorrecoesSolicitadas = ({ monografia }) => {
    const observacoesProcessadasData = processarObservacoesSupervisor(monografia.observacoes_supervisor);
    const isPrazoVencido = verificarPrazosVencidos(monografia.prazo_correcoes);

    return (
      <Box sx={{ 
        mt: 2, 
        p: { xs: 1.5, sm: 2 }, 
        bgcolor: 'warning.50', 
        borderRadius: 1, 
        border: '1px solid', 
        borderColor: 'warning.200' 
      }}>
        <Typography 
          variant={isMobile ? "subtitle1" : "h6"} 
          color="warning.main" 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 1, 
            mb: 2,
            fontWeight: 'bold'
          }}
        >
          Correções Solicitadas
        </Typography>
        
        {observacoesProcessadasData.observacoesLimpas && (
          <Box sx={{ mb: 2 }}>
            <Typography 
              variant="subtitle2" 
              sx={{ fontWeight: 'bold', mb: 1, fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              Observações do Supervisor:
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                p: { xs: 1, sm: 1.5 }, 
                bgcolor: 'grey.100', 
                borderRadius: 1,
                fontStyle: 'italic',
                border: '1px solid',
                borderColor: 'grey.300',
                lineHeight: 1.6,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' }
              }}
            >
              {observacoesProcessadasData.observacoesLimpas}
            </Typography>
          </Box>
        )}
        
        {monografia.prazo_correcoes && (
          <Box sx={{ mb: 2 }}>
            <Typography 
              variant="body2" 
              color={isPrazoVencido ? 'error.main' : 'warning.main'} 
              sx={{ 
                fontWeight: 'bold',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
                fontSize: { xs: '0.8125rem', sm: '0.875rem' }
              }}
            >
              <strong>Prazo para correções:</strong> 
              {new Date(monografia.prazo_correcoes).toLocaleDateString('pt-BR')}
              {isPrazoVencido && (
                <Chip 
                  label="VENCIDO" 
                  color="error" 
                  size="small" 
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: '0.75rem',
                    height: { xs: 20, sm: 24 }
                  }}
                />
              )}
            </Typography>
            {isPrazoVencido && (
              <Typography 
                variant="caption" 
                color="error.main" 
                sx={{ 
                  display: 'block', 
                  mt: 0.5,
                  fontStyle: 'italic',
                  fontSize: { xs: '0.75rem', sm: '0.8125rem' }
                }}
              >
                O prazo para submissão das correções já expirou. Entre em contato com seu supervisor.
              </Typography>
            )}
          </Box>
        )}
        
        {observacoesProcessadasData.temDocumento && (
          <Box sx={{ 
            mt: 2, 
            p: { xs: 1.5, sm: 2 }, 
            bgcolor: 'info.50', 
            borderRadius: 1, 
            border: '1px solid', 
            borderColor: 'info.200' 
          }}>
            <Typography 
              variant="body2" 
              color="info.main" 
              sx={{ 
                fontWeight: 'bold', 
                mb: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontSize: { xs: '0.8125rem', sm: '0.875rem' }
              }}
            >
              Documento com Correções Disponível
            </Typography>
            <Typography 
              variant="body2" 
              color="text.secondary" 
              sx={{ 
                mb: 2, 
                lineHeight: 1.5,
                fontSize: { xs: '0.75rem', sm: '0.8125rem' }
              }}
            >
              Seu supervisor anexou um documento com correções detalhadas para orientar suas revisões.
              Baixe o arquivo para visualizar as sugestões específicas.
            </Typography>
            <Button 
              size={isMobile ? "medium" : "small"}
              variant="contained" 
              color="info"
              startIcon={<DownloadIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />}
              onClick={() => downloadDocumentoCorrigido(monografia.id)}
              disabled={loadingDownload}
              sx={{ 
                textTransform: 'none',
                fontWeight: 'bold',
                borderRadius: 2,
                px: { xs: 2, sm: 3 },
                minHeight: { xs: 36, sm: 40 },
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              {loadingDownload ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={16} color="inherit" />
                  Baixando...
                </Box>
              ) : (
                'Baixar Documento Corrigido'
              )}
            </Button>
            {observacoesProcessadasData.nomeArquivo && (
              <Typography 
                variant="caption" 
                color="text.secondary" 
                sx={{ 
                  display: 'block', 
                  mt: 1,
                  fontStyle: 'italic',
                  fontSize: { xs: '0.6875rem', sm: '0.75rem' }
                }}
              >
                Arquivo: {observacoesProcessadasData.nomeArquivo}
              </Typography>
            )}
          </Box>
        )}

        <Box sx={{ 
          mt: 2, 
          p: { xs: 1, sm: 1.5 }, 
          bgcolor: 'primary.50', 
          borderRadius: 1, 
          border: '1px solid', 
          borderColor: 'primary.200' 
        }}>
          <Typography 
            variant="body2" 
            color="primary.main" 
            sx={{ 
              fontWeight: 'bold',
              mb: 1,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              fontSize: { xs: '0.8125rem', sm: '0.875rem' }
            }}
          >
            Próximos Passos:
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ 
              mt: 0.5,
              lineHeight: 1.6,
              fontSize: { xs: '0.75rem', sm: '0.8125rem' }
            }}
          >
            <strong>1.</strong> {observacoesProcessadasData.temDocumento ? 'Baixe e revise o documento corrigido anexado' : 'Revise cuidadosamente as observações do supervisor'}
            <br />
            <strong>2.</strong> Implemente todas as correções solicitadas em sua monografia
            <br />
           <strong>3.</strong> Use o botão &ldquo;Resubmeter&rdquo; para enviar a versão corrigida
            <br />
            <strong>4.</strong> Aguarde a nova análise do supervisor
          </Typography>
          
          {isPrazoVencido && (
            <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
              <Typography variant="caption" sx={{ fontSize: { xs: '0.6875rem', sm: '0.75rem' } }}>
                <strong>Atenção:</strong> O prazo expirou. Resubmeta o quanto antes e comunique-se com seu supervisor.
              </Typography>
            </Alert>
          )}
        </Box>
      </Box>
    );
  };

  SecaoCorrecoesSolicitadas.propTypes = {
    monografia: PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      observacoes_supervisor: PropTypes.string,
      prazo_correcoes: PropTypes.string,
      status: PropTypes.string,
      titulo: PropTypes.string,
      projeto: PropTypes.object,
      versao_atual: PropTypes.string,
      numero_resubmissoes: PropTypes.number,
      data_submissao: PropTypes.string,
      data_resubmissao: PropTypes.string,
      data_aprovacao_supervisor: PropTypes.string,
      observacoes_aprovacao_supervisor: PropTypes.string,
      observacoes_devolucao_direcao: PropTypes.string,
      tipo_devolucao_direcao: PropTypes.string,
      documento_monografia: PropTypes.string
    }).isRequired
  };

  // FUNÇÕES DE DOWNLOAD E VISUALIZAÇÃO DE DOCUMENTOS
  const handleDownloadDocument = async (documento, tipo) => {
    try {
      showNotification('Iniciando download do documento...', 'info');
      
      let fileUrl = null;
      let fileName = null;

      if (tipo === 'monografia') {
        fileUrl = documento.documento_monografia || documento.arquivo_url || documento.documento_url;
        fileName = `monografia_${documento.id}_${(documento.titulo || documento.projeto?.proposta?.titulo)?.substring(0, 20) || 'documento'}`;
      } else if (tipo === 'projeto') {
        fileUrl = documento.documento_projeto || documento.arquivo_url;
        fileName = `projeto_${documento.id}_${(documento.proposta?.titulo)?.substring(0, 20) || 'documento'}`;
      } else if (tipo === 'proposta') {
        fileUrl = documento.documento_proposta || documento.arquivo_url;
        fileName = `proposta_${documento.id}_${documento.titulo?.substring(0, 20) || 'documento'}`;
      }

      if (!fileUrl) {
        showNotification('Documento não encontrado para download.', 'warning');
        return;
      }

      const fileNameFromPath = fileUrl.split('\\').pop().split('/').pop();
const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.zenkroll.co.mz';
const apiUrl = `${baseUrl}/api/documentos/${fileNameFromPath}`;

      const token = localStorage.getItem('token');
      if (!token) {
        showNotification('Sessão expirada. Faça login novamente.', 'error');
        return;
      }

      showNotification('Processando download...', 'info');

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.status === 401) {
        showNotification('Sessão expirada. Faça login novamente.', 'error');
        return;
      }

      if (!response.ok) {
        throw new Error(`Erro HTTP: ${response.status} - ${response.statusText}`);
      }

      const blob = await response.blob();

      if (blob.size === 0) {
        throw new Error('Arquivo vazio recebido do servidor.');
      }

      const originalExtension = fileNameFromPath.split('.').pop();
      const finalFileName = `${fileName}.${originalExtension}`;

      const objectUrl = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = objectUrl;
      downloadLink.download = finalFileName;
      downloadLink.style.display = 'none';
      
      document.body.appendChild(downloadLink);
      downloadLink.click();
      
      setTimeout(() => {
        document.body.removeChild(downloadLink);
        URL.revokeObjectURL(objectUrl);
      }, 100);

      showNotification(`Download concluído: ${finalFileName}`, 'success');

    } catch (error) {
      handleApiError(error, 'download do documento');
    }
  };

  const handleViewDocument = async (documento, tipo) => {
    try {
      showNotification('Abrindo documento...', 'info');
      
      let fileUrl = null;
      
      if (tipo === 'monografia') {
        fileUrl = documento.documento_monografia || documento.arquivo_url || documento.documento_url;
      } else if (tipo === 'projeto') {
        fileUrl = documento.documento_projeto || documento.arquivo_url;
      } else if (tipo === 'proposta') {
        fileUrl = documento.documento_proposta || documento.arquivo_url;
      }

      if (!fileUrl) {
        showNotification('Documento não disponível para visualização.', 'warning');
        return;
      }

      const fileNameFromPath = fileUrl.split('\\').pop().split('/').pop();
      const fileExtension = fileNameFromPath.split('.').pop().toLowerCase();
  const baseUrl = window.location.hostname === 'localhost' ? 'http://localhost:5000' : 'https://api.zenkroll.co.mz';
const publicUrl = `${baseUrl}/api/documentos/view/${fileNameFromPath}`;

      if (fileExtension === 'pdf') {
        window.open(publicUrl, '_blank');
        showNotification('Documento PDF aberto em nova aba!', 'success');
      } else if (['docx', 'doc', 'xlsx', 'xls', 'pptx', 'ppt'].includes(fileExtension)) {
        const userChoice = window.confirm(
          `Documento: ${fileNameFromPath}

Os documentos Word/Excel não podem ser visualizados diretamente no navegador.

Clique OK para baixar e abrir no seu computador.
Clique Cancelar para tentar visualizar online (pode não funcionar).`
        );
        
        if (userChoice) {
          handleDownloadDocument(documento, tipo);
        } else {
          const googleViewerUrl = `https://docs.google.com/gviewer?url=${encodeURIComponent(publicUrl)}&embedded=true`;
          window.open(googleViewerUrl, '_blank', 'width=1200,height=800');
          showNotification('Tentando visualizar documento online...', 'info');
        }
      } else {
        window.open(publicUrl, '_blank');
        showNotification('Documento aberto em nova aba!', 'success');
      }

    } catch (error) {
      handleApiError(error, 'visualização do documento');
    }
  };

  // Função para carregar as abas disponíveis
  const loadAvailableTabs = useCallback(async () => {
    if (!user) return;
    
    setLoadingTabs(true);
    showNotification('Verificando módulos disponíveis...', 'info');
    
    try {
      const moduleTabMap = {
        'CORE': [0, 1, 2, 4, 5],
        'PAGAMENTOS': [3],
        'TCC_MONOGRAFIAS': [6],
      };
      
      const [hasCoreAccess, hasPagamentosAccess, hasTccAccess] = await Promise.all([
        checkModuleAccess('CORE'),
        checkModuleAccess('PAGAMENTOS'),
        checkModuleAccess('TCC_MONOGRAFIAS')
      ]);
      
      console.log('Acesso aos módulos:', {
        CORE: hasCoreAccess,
        PAGAMENTOS: hasPagamentosAccess,
        TCC_MONOGRAFIAS: hasTccAccess
      });
      
      let availableTabIndexes = new Set();
      
      if (hasCoreAccess) {
        moduleTabMap['CORE'].forEach(tabIndex => availableTabIndexes.add(tabIndex));
      }
      
      if (hasPagamentosAccess) {
        moduleTabMap['PAGAMENTOS'].forEach(tabIndex => availableTabIndexes.add(tabIndex));
      }
      
      if (hasTccAccess) {
        moduleTabMap['TCC_MONOGRAFIAS'].forEach(tabIndex => availableTabIndexes.add(tabIndex));
      }
      
      const tabIndexes = Array.from(availableTabIndexes).sort();
      
      const allTabs = [
        { icon: <CampaignIcon />, label: "Campanhas", value: 0 },
        { icon: <AssignmentIcon />, label: "Pré-inscrições", value: 1 },
        { icon: <CalendarIcon />, label: "Prazos", value: 2 },
        { icon: <PaymentIcon />, label: "Pagamentos", value: 3 },
        { icon: <NotificationsIcon />, label: "Notificações", value: 4 },
        { icon: <RecommendIcon />, label: "Recomendações", value: 5 },
        { icon: <AssignmentIndIcon />, label: "TCC/Monografia", value: 6 }
      ];
      
      const tabs = allTabs.filter(tab => tabIndexes.includes(tab.value));
      
      setAvailableTabs(tabs);
      
      if (tabs.length > 0 && !tabs.some(tab => tab.value === value)) {
        setValue(tabs[0].value);
      }

      showNotification(`${tabs.length} módulos carregados com sucesso!`, 'success');
    } catch (error) {
      console.error('Erro ao carregar abas disponíveis:', error);
      handleApiError(error, 'carregamento dos módulos');
      setAvailableTabs([
        { icon: <CampaignIcon />, label: "Campanhas", value: 0 },
        { icon: <AssignmentIcon />, label: "Pré-inscrições", value: 1 },
        { icon: <NotificationsIcon />, label: "Notificações", value: 4 },
        { icon: <RecommendIcon />, label: "Recomendações", value: 5 }
      ]);
    } finally {
      setLoadingTabs(false);
    }
  }, [user, value, checkModuleAccess]);

  // Função para buscar dados do dashboard
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      showNotification('Carregando dados do painel...', 'info');
      
      const currentPage = pageNumbers[value];
      
      const response = await api.get('/dashboard/estudante', {
        params: { page: currentPage, itemsPerPage: ITEMS_PER_PAGE, tab: value }
      });

      setDashboardData({
        ...response.data,
        proposta: response.data.proposta || null
      });

      if (value === 0) {
        setTotalPages(prev => ({ ...prev, 0: Math.ceil(response.data.totalCampanhas / ITEMS_PER_PAGE) }));
      } else if (value === 1) {
        setTotalPages(prev => ({ ...prev, 1: Math.ceil(response.data.totalPreInscricoes / ITEMS_PER_PAGE) }));
      } else if (value === 3) {
        setTotalPages(prev => ({ ...prev, 3: Math.ceil(response.data.totalPagamentos / ITEMS_PER_PAGE) }));
      }

      if (value === 4) {
        await fetchNotificacoes();
      }

      setDashboardError('');
      showNotification('Dados carregados com sucesso!', 'success');
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      const errorMessage = handleApiError(error, 'carregamento dos dados');
      setDashboardError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [pageNumbers, value, fetchNotificacoes]);

  // Função para buscar dados de recomendações
  const fetchDadosRecomendacao = useCallback(async () => {
    if (!user?.id) return;
    try {
      showNotification('Carregando recomendações personalizadas...', 'info');
      
      console.log('Iniciando busca de recomendações para usuário:', user.id);
      const [recomendacoesRes, perfilRes] = await Promise.all([
        api.get(`/recomendacoes/${user.id}`),
        api.get(`/perfil-academico/${user.estudanteId}`)
      ]);
      
      const recomendacoesData = recomendacoesRes.data?.recomendacoes || [];
      const recomendacoesFormatadas = recomendacoesData.map(rec => {
        const justificativa = rec.justificativa || '';
        let nomeCurso = 'Nome do curso não disponível';
        let nomeInstituicao = 'Nome da instituição não disponível';
        if (justificativa.includes('Curso: ')) {
          const cursoPart = justificativa.split('Curso: ')[1];
          if (cursoPart.includes(' na ')) {
            nomeCurso = cursoPart.split(' na ')[0];
            nomeInstituicao = cursoPart.split(' na ')[1];
          } else {
            nomeCurso = cursoPart;
          }
        }
        return {
          id: rec.cursoId,
          cursoId: rec.cursoId,
          estudanteId: rec.estudanteId,
          pontuacaoAcademica: Number(rec.pontuacaoAcademica),
          pontuacaoGeografica: Number(rec.pontuacaoGeografica),
          pontuacaoTotal: Number(rec.pontuacaoTotal),
          justificativa: rec.justificativa,
          nivel: rec.nivel,
          curso: {
            id: rec.cursoId,
            nome: nomeCurso,
            instituicao: { nome: nomeInstituicao }
          }
        };
      });
      
      setRecomendacoes(recomendacoesFormatadas);
      
      if (perfilRes.data) {
        setPerfilAcademico({
          tipoEnsinoMedio: perfilRes.data.tipoEnsinoMedio,
          areasDestaque: perfilRes.data.areasDestaque || [],
          mediasNotas: perfilRes.data.mediasNotas
        });
      }
      
      console.log('Recomendações processadas:', recomendacoesFormatadas);
      showNotification(`${recomendacoesFormatadas.length} recomendações carregadas!`, 'success');
    } catch (error) {
      console.error('Erro ao carregar dados de recomendações:', error);
      handleApiError(error, 'carregamento das recomendações');
    }
  }, [user?.id, user?.estudanteId]);

  // CARREGAMENTO DE DADOS TCC
  const fetchPropostasData = useCallback(async () => {
    try {
      setLoadingPropostas(true);
      showNotification('Carregando suas propostas...', 'info');
      
      const response = await api.get('/propostas');
      setPropostas(response.data || []);
      setErrorPropostas(null);
      showNotification(`${response.data?.length || 0} propostas carregadas!`, 'success');
    } catch (error) {
      console.error('Erro ao carregar propostas:', error);
      const errorMessage = handleApiError(error, 'carregamento das propostas');
      setErrorPropostas(errorMessage);
    } finally {
      setLoadingPropostas(false);
    }
  }, []);

  const carregarProjetos = async () => {
    try {
      setLoadingProjetos(true);
      showNotification('Carregando seus projetos...', 'info');
      
      const response = await listarProjetos(user.id);
      setProjetos(response.data);
      setErrorProjetos(null);
      showNotification(`${response.data?.length || 0} projetos carregados!`, 'success');
    } catch (err) {
      console.error('Erro ao carregar projetos:', err);
      const errorMessage = handleApiError(err, 'carregamento dos projetos');
      setErrorProjetos(errorMessage);
    } finally {
      setLoadingProjetos(false);
    }
  };

  const carregarMonografias = async () => {
    try {
      setLoadingMonografias(true);
      showNotification('Carregando suas monografias...', 'info');

      const response = await listarMonografias(user.id);
      setMonografias(response.data);

      if (response.data && response.data.length > 0) {
        await verificarDocumentosCorrigidos(response.data);

        // Verificar se alguma monografia está arquivada
        try {
          const arquivos = await listarArquivosDigitais();
          const monografiaIds = response.data.map(m => m.id);
          const meuArquivo = Array.isArray(arquivos)
            ? arquivos.find(a => monografiaIds.includes(a.monografia_id))
            : null;
          setArquivoDigital(meuArquivo || null);
        } catch (_) { /* silencioso — widget é opcional */ }
      }

      setErrorMonografias(null);
      showNotification(`${response.data?.length || 0} monografias carregadas!`, 'success');
    } catch (err) {
      console.error('Erro ao carregar monografias:', err);
      const errorMessage = handleApiError(err, 'carregamento das monografias');
      setErrorMonografias(errorMessage);
    } finally {
      setLoadingMonografias(false);
    }
  };

  const carregarDefesas = async () => {
    try {
      setLoadingDefesas(true);
      showNotification('Carregando suas defesas...', 'info');
      
      const response = await listarDefesas(user.id);
      setDefesas(response.data || []);
      setErrorDefesas(null);
      showNotification(`${response.data?.length || 0} defesas carregadas!`, 'success');
    } catch (err) {
      console.error('Erro ao carregar defesas:', err);
      const errorMessage = handleApiError(err, 'carregamento das defesas');
      setErrorDefesas(errorMessage);
    } finally {
      setLoadingDefesas(false);
    }
  };

  const carregarAvaliacoes = async () => {
    try {
      setLoadingAvaliacoes(true);
      showNotification('Carregando suas avaliações...', 'info');
      
      const response = await listarAvaliacoes(user.id);
      setAvaliacoes(response.data);
      setErrorAvaliacoes(null);
      showNotification(`${response.data?.length || 0} avaliações carregadas!`, 'success');
    } catch (err) {
      console.error('Erro ao carregar avaliações:', err);
      const errorMessage = handleApiError(err, 'carregamento das avaliações');
      setErrorAvaliacoes(errorMessage);
    } finally {
      setLoadingAvaliacoes(false);
    }
  };

  // FUNÇÕES DE AÇÃO PARA MONOGRAFIAS
  const handleVoltarParaRevisao = async (monografiaId) => {
    try {
      setSubmittingAction(true);
      showNotification('Reenviando monografia para revisão...', 'info');
      
      await voltarMonografiaParaRevisao(monografiaId);
      showNotification('Monografia reenviada para revisão com sucesso!', 'success');
      await carregarMonografias();
    } catch (error) {
      console.error(error);
      handleApiError(error, 'reenvio da monografia para revisão');
    } finally {
      setSubmittingAction(false);
    }
  };

const handleResubmeterMonografia = async (monografia) => {
  try {
    showNotification('Redirecionando para resubmissão da monografia...', 'info');
    
    // Navegar para página existente de submissão com estado de resubmissão
    navigate('/monografias/submeter', { 
      state: { 
        resubmissao: true, 
        monografiaId: monografia.id,
        monografia: monografia,
        titulo: monografia.projeto?.proposta?.titulo || monografia.titulo,
        versaoAtual: monografia.versao_atual
      }
    });
    
  } catch (error) {
    console.error('Erro ao redirecionar para resubmissão:', error);
    showNotification('Erro ao abrir página de resubmissão', 'error');
  }
};
  const handleSubmeterParaDirecao = async (monografia) => {
    try {
      setSubmittingAction(true);
      showNotification('Submetendo monografia à Direção Científica...', 'info');
      
      await submeterParaDirecaoCientifica(monografia.id);
      
      showNotification('Monografia submetida à Direção Científica com sucesso!', 'success');
      await carregarMonografias();
    } catch (error) {
      console.error('Erro ao submeter à Direção Científica:', error);
      handleApiError(error, 'submissão à Direção Científica');
    } finally {
      setSubmittingAction(false);
    }
  };
  // Effects
  useEffect(() => {
    if (user) {
      loadAvailableTabs();
    }
  }, [user, loadAvailableTabs]);

  useEffect(() => {
    const neverShow = localStorage.getItem('neverShowRecomendacoesModal');
    console.log('Valor de neverShowRecomendacoesModal no localStorage:', neverShow);
    if (neverShow === 'true') {
      setNeverShowModal(true);
      setShowRecomendacoesModal(false);
    } else {
      setNeverShowModal(false);
      setShowRecomendacoesModal(true);
    }
  }, []);

  useEffect(() => {
    if (recomendacoes.length > 0) {
      const hasHighMatch = recomendacoes.some(rec => rec.pontuacaoTotal >= 70);
      setShowRecomendacoesAlert(true);
    }
  }, [recomendacoes]);

  useEffect(() => {
    if (user && !loadingTabs) {
      fetchData();
      
      const hasRecomendacoesTab = availableTabs.some(tab => tab.value === 5);
      if (hasRecomendacoesTab) {
        fetchDadosRecomendacao();
      }
    }
  }, [user, fetchData, fetchDadosRecomendacao, loadingTabs, availableTabs]);

  useEffect(() => {
    const fetchPropostas = async () => {
      const hasTccTab = availableTabs.some(tab => tab.value === 6);
      if (value === 6 && hasTccTab) {
        await fetchPropostasData();
      }
    };
    
    if (user?.id && !loadingTabs) {
      fetchPropostas();
    }
  }, [value, user?.id, availableTabs, loadingTabs, fetchPropostasData]);

  useEffect(() => {
    const hasTccTab = availableTabs.some(tab => tab.value === 6);
    if (tccTab === 1 && hasTccTab && value === 6) {
      carregarProjetos();
    }
  }, [tccTab, value, availableTabs]);

  useEffect(() => {
    const hasTccTab = availableTabs.some(tab => tab.value === 6);
    if (hasTccTab && value === 6) {
      if (tccTab === 2) {
        carregarMonografias();
      } else if (tccTab === 3) {
        carregarDefesas();
      } else if (tccTab === 4) {
        carregarAvaliacoes();
      }
    }
  }, [tccTab, value, availableTabs]);

  // Handlers
  const handleRefreshNotificacoes = async () => {
    if (value === 4) {
      showNotification('Atualizando notificações...', 'info');
      try {
        await fetchNotificacoes();
        showNotification('Notificações atualizadas!', 'success');
      } catch (error) {
        handleApiError(error, 'atualização das notificações');
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
    setPageNumbers(prev => ({ ...prev, [newValue]: 1 }));
  };

  const handlePageChange = (event, newPage) => {
    setPageNumbers(prev => ({ ...prev, [value]: newPage }));
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleDrawerToggle = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleLogout = () => {
    handleCloseMenu();
    showNotification('Encerrando sessão...', 'info');
    logout();
    navigate('/login');
  };

  const handleEditProfile = () => {
    handleCloseMenu();
    navigate('/perfil');
  };

  const handleNeverShowAgain = () => {
    console.log('Usuário optou por nunca mais ver o Modal.');
    localStorage.setItem('neverShowRecomendacoesModal', 'true');
    setNeverShowModal(true);
    setShowRecomendacoesModal(false);
showNotification('Preferência salva! O modal não será mais exibido.', 'success');
  };

  const handleShowLater = () => {
    console.log('Usuário optou por ver o Modal depois.');
    setShowRecomendacoesModal(false);
    showNotification('Modal fechado temporariamente.', 'info');
  };

  const handleVerRecomendacoes = () => {
    console.log('Usuário clicou em Ver Recomendações.');
    setShowRecomendacoesModal(false);
    setShowRecomendacoesAlert(false);
    showNotification('Redirecionando para recomendações...', 'info');
    navigate('/recomendacoes');
  };

  // Função para abrir o chat de forma genérica
  const openChatDialogHandler = async (item, context = 'projeto') => {
    console.log(`Abrindo chat para o ${context}:`, item);
    setSelectedChatItem({ data: item, context });
    setChatDialogOpen(true);
    setLoadingChat(true);
    showNotification('Carregando histórico de mensagens...', 'info');
    
    try {
      const response = await api.get('/chat/messages', {
        params: { context, context_id: item.id }
      });
      setChatHistory(response.data);
      showNotification('Chat carregado com sucesso!', 'success');
    } catch (error) {
      console.error("Erro ao carregar histórico de chat:", error);
      handleApiError(error, 'carregamento do chat');
    } finally {
      setLoadingChat(false);
    }
  };

  // Função para enviar mensagem no chat
  const sendChatMessageHandler = async () => {
    if (chatMessage.trim() === '') {
      showNotification('Digite uma mensagem antes de enviar.', 'warning');
      return;
    }
    
    const payload = {
      context: selectedChatItem?.context || 'projeto',
      context_id: selectedChatItem?.data?.id,
      sender: user?.nome,
      message: chatMessage
    };
    
    console.log("Payload enviado:", payload);
    showNotification('Enviando mensagem...', 'info');
    
    try {
      await api.post('/chat/messages', payload);
      setChatMessage('');
      const response = await api.get('/chat/messages', {
        params: { 
          context: selectedChatItem?.context || 'projeto', 
          context_id: selectedChatItem?.data?.id 
        }
      });
      setChatHistory(response.data);
      showNotification('Mensagem enviada com sucesso!', 'success');
    } catch (error) {
      console.error("Erro ao enviar mensagem:", error);
      handleApiError(error, 'envio da mensagem');
    }
  };

  const renderNotificationsIcon = () => {
    return (
      <IconButton 
        color="inherit"
        onClick={() => {
          const notificationsTabIndex = availableTabs.findIndex(tab => tab.value === 4);
          if (notificationsTabIndex !== -1) {
            setValue(4);
            showNotification('Abrindo notificações...', 'info');
          }
        }}
        title="Notificações"
        sx={{ 
          minHeight: { xs: 40, sm: 48 },
          minWidth: { xs: 40, sm: 48 }
        }}
      >
        <Badge badgeContent={naoLidas} color="error">
          <NotificationsIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
        </Badge>
      </IconButton>
    );
  };

  // Renderização do carrossel mobile-friendly
  const renderMobileCarousel = (items, renderItem, emptyMessage, emptyIcon) => {
    if (!items || items.length === 0) {
      return (
        <Alert 
          severity="info" 
          icon={emptyIcon}
          sx={{ m: 2, borderRadius: 2 }}
        >
          {emptyMessage}
        </Alert>
      );
    }

    if (isMobile) {
      return (
        <Box sx={{ p: 1 }}>
          {items.map((item, index) => (
            <Box key={item.id || index} sx={{ mb: 2 }}>
              {renderItem(item)}
            </Box>
          ))}
        </Box>
      );
    }

    return (
      <Slider {...getCarouselSettings(isMobile)}>
        {items.map((item, index) => (
          <Box key={item.id || index} sx={{ p: 2 }}>
            {renderItem(item)}
          </Box>
        ))}
      </Slider>
    );
  };

  const renderCampanhas = () => {
    const { campanhasDisponiveis } = dashboardData;
    
    const renderCampanha = (campanha) => {
      const instituicaoId = campanha.instituicao?.id || campanha.instituicaoId;
      const isExpired = new Date(campanha.dataFim) < new Date();
      
      return (
        <ExpandableCard
          title={campanha.nome}
          subtitle={`${campanha.instituicao?.nome || campanha.instituicaoNome}`}
          expanded={!isMobile}
          statusChip={
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              <Chip
                icon={<StarIcon />}
                label={`${campanha.vagasDisponiveis} vagas`}
                size={isMobile ? "medium" : "small"}
                color="primary"
                variant="outlined"
              />
              <Chip
                icon={<CalendarIcon />}
                label={`Prazo: ${new Date(campanha.dataFim).toLocaleDateString()}`}
                size={isMobile ? "medium" : "small"}
                color={isExpired ? "error" : "warning"}
                variant="outlined"
              />
            </Box>
          }
          actions={[
            <Button
              key="detalhes"
              size={isMobile ? "medium" : "small"}
              variant="outlined"
              onClick={() => {
                showNotification('Abrindo detalhes...', 'info');
                navigate(`/campanhas/${campanha.id}`);
              }}
              sx={{ 
                flex: isMobile ? 1 : 'none',
                minHeight: 40
              }}
            >
              Ver Detalhes
            </Button>,
            <Button
              key="inscricao"
              size={isMobile ? "medium" : "small"}
              variant="contained"
              onClick={() => {
                if (isExpired) {
                  showNotification('Esta campanha já expirou!', 'warning');
                } else {
                  showNotification('Redirecionando para pré-inscrição...', 'info');
                  navigate(`/pre-inscricoes/criar?campanhaId=${campanha.id}`);
                }
              }}
              disabled={isExpired}
              sx={{ 
                flex: isMobile ? 1 : 'none',
                minHeight: 40
              }}
            >
              {isExpired ? 'Expirada' : 'Pré-inscrever-se'}
            </Button>,
            ...(instituicaoId ? [
              <Button
                key="instituicao"
                size="small"
                sx={{ 
                  color: 'primary.main',
                  textTransform: 'none',
                  minHeight: 36,
                  width: isMobile ? '100%' : 'auto'
                }}
                endIcon={<ChevronRightIcon />}
                onClick={() => {
                  showNotification('Abrindo informações da instituição...', 'info');
                  navigate(`/instituicoes/${instituicaoId}/visualizar`);
                }}
              >
                Saiba Mais Sobre a Instituição
              </Button>
            ] : [])
          ]}
        />
      );
    };

    return renderMobileCarousel(
      campanhasDisponiveis, 
      renderCampanha,
      "Não há campanhas disponíveis no momento. Volte mais tarde para verificar novas oportunidades!",
      <CampaignIcon />
    );
  };

  const renderPreInscricoes = () => {
    const { preInscricoesRecentes } = dashboardData;
    
    const renderInscricao = (inscricao) => (
      <ExpandableCard
        title={inscricao.campanha.nome}
        subtitle={`Data: ${new Date(inscricao.createdAt).toLocaleDateString()}`}
        expanded={false}
        statusChip={
          <Chip
            label={inscricao.status}
            size="small"
            color={
              inscricao.status === 'APROVADA' ? 'success' :
              inscricao.status === 'PENDENTE' ? 'warning' :
              inscricao.status === 'REJEITADA' ? 'error' : 'default'
            }
          />
        }
        actions={[
          <Button 
            key="detalhes"
            size={isMobile ? "medium" : "small"}
            variant="outlined"
            onClick={() => {
              showNotification('Abrindo detalhes da pré-inscrição...', 'info');
              navigate(`/pre-inscricoes/${inscricao.id}`);
            }}
            sx={{ 
              flex: isMobile ? 1 : 'none',
              minHeight: 40
            }}
          >
            Ver Detalhes
          </Button>,
          ...(inscricao.status === 'APROVADA' ? [
            <Button
              key="comprovativo"
              size={isMobile ? "medium" : "small"}
              variant="contained"
              onClick={() => {
                showNotification('Gerando comprovativo...', 'info');
                navigate(`/pre-inscricao-comprovativo/${inscricao.id}`);
              }}
              sx={{ 
                flex: isMobile ? 1 : 'none',
                minHeight: 40
              }}
            >
              Gerar Comprovativo
            </Button>
          ] : [])
        ]}
      />
    );

    return renderMobileCarousel(
      preInscricoesRecentes,
      renderInscricao,
      "Você ainda não tem pré-inscrições realizadas. Explore as campanhas disponíveis!",
      <AssignmentIcon />
    );
  };

  const renderProximosPrazos = () => {
    const { proximosPrazos } = dashboardData;
    
    const renderPrazo = (prazo, index) => (
      <ExpandableCard
        key={index}
        title={prazo.titulo}
        subtitle={`Data: ${new Date(prazo.data).toLocaleDateString()}`}
        expanded={false}
        actions={prazo.campanhaId ? [
          <Button 
            key="campanha"
            size={isMobile ? "medium" : "small"}
            variant="outlined"
            onClick={() => {
              showNotification('Abrindo campanha relacionada...', 'info');
              navigate(`/campanhas/${prazo.campanhaId}`);
            }}
            sx={{ 
              flex: isMobile ? 1 : 'none',
              minHeight: 40
            }}
          >
            Ver Campanha
          </Button>
        ] : undefined}
      >
        <Typography variant="body2" color="text.secondary">
          {prazo.descricao}
        </Typography>
      </ExpandableCard>
    );

    return renderMobileCarousel(
      proximosPrazos,
      renderPrazo,
      "Não há prazos próximos para exibir. Mantenha-se atualizado!",
      <CalendarIcon />
    );
  };

  const renderPagamentos = () => {
    return (
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
          <Button
            variant="contained"
            startIcon={<PaymentIcon />}
            size={isMobile ? "medium" : "small"}
            onClick={() => {
              showNotification('Abrindo planos de pagamento...', 'info');
              navigate('/estudante/planos-pagamento');
            }}
            sx={{ 
              minHeight: 40,
              width: { xs: '100%', sm: 'auto' }
            }}
          >
            Ver Plano Completo
          </Button>
        </Box>
        <PlanosPagamentoEstudante />
      </Box>
    );
  };

  const renderRecomendacoes = () => (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 2,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0
      }}>
        <Typography variant={isMobile ? "h6" : "h5"}>
          Recomendações Vocacionais
        </Typography>
        <IconButton 
          onClick={() => {
            showNotification('Atualizando recomendações...', 'info');
            fetchDadosRecomendacao();
          }} 
          title="Atualizar recomendações"
          sx={{ minHeight: 40, minWidth: 40 }}
        >
          <RefreshIcon />
        </IconButton>
      </Box>
      <RecomendacoesOverview 
        recomendacoes={recomendacoes}
        perfil={perfilAcademico}
        minimal={true}
        isLoading={loading}
      />
      {(!recomendacoes || recomendacoes.length === 0) && !loading && (
        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }} icon={<RecommendIcon />}>
          Não há recomendações disponíveis no momento. 
          {!perfilAcademico && " Complete seu perfil para receber recomendações personalizadas."}
        </Alert>
      )}
    </Box>
  );

  // RENDERIZAÇÃO DOS COMPONENTES TCC MOBILE
  const renderPropostasTCC = () => {
    const canSubmitNew = !propostas.some(p => p.status === 'PENDENTE');
    
    return (
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center', 
            mb: 3,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0
          }}>
            <Typography variant="h6">Propostas de TCC</Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={() => {
                showNotification('Redirecionando para submissão...', 'info');
                navigate('/propostas/submeter');
              }}
              disabled={!canSubmitNew}
              size={isMobile ? "medium" : "small"}
              sx={{ 
                minHeight: 40,
                width: isMobile ? '100%' : 'auto'
              }}
            >
              Nova Proposta
            </Button>
          </Box>

          {!canSubmitNew && (
            <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
              Você já tem uma proposta pendente de avaliação. Aguarde a análise antes de submeter uma nova.
            </Alert>
          )}

          {loadingPropostas ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : errorPropostas ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {errorPropostas}
              <Button 
                color="inherit" 
                size="small" 
                onClick={() => window.location.reload()} 
                sx={{ ml: 2, minHeight: 32 }}
              >
                Tentar Novamente
              </Button>
            </Alert>
          ) : propostas.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              {propostas.map((proposta) => (
                <ExpandableCard
                  key={proposta.id}
                  title={proposta.titulo}
                  subtitle={`Submetida em: ${new Date(proposta.dataSubmissao).toLocaleDateString()}`}
                  expanded={!isMobile}
                  statusChip={
                    <Chip 
                      label={proposta.status}
                      color={
                        proposta.status === 'APROVADA' ? 'success' :
                        proposta.status === 'REJEITADA' ? 'error' : 'warning'
                      }
                      size="small"
                    />
                  }
                  actions={[
                    ...(proposta.documento_proposta ? [
                      <Button
                        key="view"
                        variant="outlined"
                        size={isMobile ? "medium" : "small"}
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewDocument(proposta, 'proposta')}
                        sx={{ minHeight: 40 }}
                      >
                        Ver Doc
                      </Button>,
                      <Button
                        key="download"
                        variant="outlined"
                        size={isMobile ? "medium" : "small"}
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadDocument(proposta, 'proposta')}
                        sx={{ minHeight: 40 }}
                      >
                        Download
                      </Button>
                    ] : []),
                    <Button
                      key="details"
                      variant="outlined"
                      size={isMobile ? "medium" : "small"}
                      endIcon={<ChevronRightIcon />}
                      onClick={() => navigate(`/propostas/${proposta.id}`)}
                      sx={{ 
                        flex: isMobile ? 1 : 'none',
                        minHeight: 40
                      }}
                    >
                      Ver Detalhes
                    </Button>,
                    <Button
                      key="chat"
                      variant="outlined"
                      size={isMobile ? "medium" : "small"}
                      startIcon={<ChatIcon />}
                      onClick={() => openChatDialogHandler(proposta, 'proposta')}
                      sx={{ minHeight: 40 }}
                    >
                      Chat
                    </Button>
                  ]}
                >
                  {proposta.status === 'REJEITADA' && proposta.observacoes && (
                    <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2">Motivo da Rejeição:</Typography>
                      <Typography variant="body2">{proposta.observacoes}</Typography>
                    </Alert>
                  )}

                  {proposta.status === 'APROVADA' && proposta.observacoes && (
                    <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2">Observações:</Typography>
                      <Typography variant="body2">{proposta.observacoes}</Typography>
                    </Alert>
                  )}
                </ExpandableCard>
              ))}
            </Box>
          ) : (
            <Alert severity="info" icon={<ArticleIcon />} sx={{ borderRadius: 2 }}>
              Você ainda não submeteu nenhuma proposta de TCC. Clique em Nova Proposta para começar!
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };
  const renderProjetosTCC = () => {
    const canSubmitProject = propostas.some(p => p.status === 'APROVADA' && !p.projeto);
    const hasApprovedProposal = propostas.some(p => p.status === 'APROVADA');
    
    return (
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center', 
            mb: 3,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0
          }}>
            <Typography variant="h6">Projetos de TCC</Typography>
            {canSubmitProject && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  showNotification('Redirecionando para submissão do projeto...', 'info');
                  navigate('/projetos/submeter');
                }}
                size={isMobile ? "medium" : "small"}
                sx={{ 
                  minHeight: 40,
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                Submeter Projeto
              </Button>
            )}
          </Box>

          {!hasApprovedProposal && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Para submeter um projeto, você precisa primeiro ter uma proposta aprovada.
            </Alert>
          )}

          {loadingProjetos ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : errorProjetos ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {errorProjetos}
              <Button 
                color="inherit" 
                size="small" 
                onClick={carregarProjetos} 
                sx={{ ml: 2, minHeight: 32 }}
              >
                Tentar Novamente
              </Button>
            </Alert>
          ) : projetos.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              {projetos.map((projeto) => (
                <ExpandableCard
                  key={projeto.id}
                  title={projeto.proposta.titulo}
                  subtitle={`Submetido em: ${new Date(projeto.created_at).toLocaleDateString()}`}
                  expanded={!isMobile}
                  statusChip={
                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                      <Chip 
                        label={projeto.status}
                        color={
                          projeto.status === 'APROVADO' ? 'success' :
                          projeto.status === 'PENDENTE' ? 'warning' : 'error'
                        }
                        size="small"
                      />
                      {projeto.status === 'APROVADO' && (
                        <Chip 
                          variant="outlined"
                          size="small"
                          label={`Aprovado em: ${new Date(projeto.updated_at).toLocaleDateString()}`}
                        />
                      )}
                    </Box>
                  }
                  actions={[
                    ...(projeto.documento_projeto ? [
                      <Button
                        key="view"
                        variant="outlined"
                        size={isMobile ? "medium" : "small"}
                        startIcon={<VisibilityIcon />}
                        onClick={() => handleViewDocument(projeto, 'projeto')}
                        sx={{ minHeight: 40 }}
                      >
                        Ver Doc
                      </Button>,
                      <Button
                        key="download"
                        variant="outlined"
                        size={isMobile ? "medium" : "small"}
                        startIcon={<DownloadIcon />}
                        onClick={() => handleDownloadDocument(projeto, 'projeto')}
                        sx={{ minHeight: 40 }}
                      >
                        Download
                      </Button>
                    ] : []),
                    <Button
                      key="details"
                      variant="outlined"
                      size={isMobile ? "medium" : "small"}
                      endIcon={<ChevronRightIcon />}
                      onClick={() => navigate(`/projetos/${projeto.id}`)}
                      sx={{ 
                        flex: isMobile ? 1 : 'none',
                        minHeight: 40
                      }}
                    >
                      Ver Detalhes
                    </Button>,
                    <Button
                      key="chat"
                      variant="outlined"
                      size={isMobile ? "medium" : "small"}
                      startIcon={<ChatIcon />}
                      onClick={() => openChatDialogHandler(projeto, 'projeto')}
                      sx={{ minHeight: 40 }}
                    >
                      Chat
                    </Button>
                  ]}
                >
                  {projeto.observacoes && projeto.status === 'APROVADO' && (
                    <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                      <Typography variant="subtitle2">Observações:</Typography>
                      <Typography variant="body2">{projeto.observacoes}</Typography>
                    </Alert>
                  )}
                </ExpandableCard>
              ))}
            </Box>
          ) : (
            <Alert severity="info" icon={<AssignmentIcon />} sx={{ borderRadius: 2 }}>
              {hasApprovedProposal 
                ? 'Você tem propostas aprovadas. Submeta seu projeto!'
                : 'Você ainda não tem projetos submetidos.'}
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderMonografias = () => {
    const canSubmitMonografia = propostas.some(p => p.projeto?.status === 'APROVADO' && !p.projeto?.monografia);
    const hasApprovedProject = propostas.some(p => p.projeto?.status === 'APROVADO');

    return (
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: isMobile ? 'flex-start' : 'center', 
            mb: 3,
            flexDirection: isMobile ? 'column' : 'row',
            gap: isMobile ? 2 : 0
          }}>
            <Typography variant="h6">Minhas Monografias</Typography>
            {canSubmitMonografia && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<UploadIcon />}
                onClick={() => {
                  showNotification('Redirecionando para submissão da monografia...', 'info');
                  navigate('/monografias/submeter');
                }}
                size={isMobile ? "medium" : "small"}
                sx={{ 
                  minHeight: 40,
                  width: isMobile ? '100%' : 'auto'
                }}
              >
                Submeter Monografia
              </Button>
            )}
          </Box>

          {/* Widget: Meu Arquivo Digital */}
          {arquivoDigital ? (
            <Box
              sx={{
                mb: 3,
                p: 2.5,
                borderRadius: 3,
                background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                color: 'white',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 2, p: 1, display: 'flex' }}>
                  <LibraryBooksIcon />
                </Box>
                <Box>
                  <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
                    REPOSITÓRIO DIGITAL
                  </Typography>
                  <Typography variant="body1" fontWeight={600}>
                    A tua monografia está arquivada
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.75)' }}>
                    Arquivada em {new Date(arquivoDigital.data_indexacao).toLocaleDateString('pt-PT')}
                  </Typography>
                </Box>
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<OpenInNewIcon fontSize="small" />}
                onClick={() => navigate('/repositorio')}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '1px solid rgba(255,255,255,0.4)',
                  borderRadius: 2,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
                  whiteSpace: 'nowrap',
                }}
              >
                Ver no Repositório
              </Button>
            </Box>
          ) : monografias.some(m => m.status === 'APROVADA') ? (
            <Alert
              severity="info"
              icon={<Inventory2Icon />}
              sx={{ mb: 3, borderRadius: 2 }}
              action={
                <Button size="small" color="inherit" onClick={() => navigate('/repositorio')}>
                  Ver Repositório
                </Button>
              }
            >
              A tua monografia foi aprovada. Procede ao arquivamento no Repositório Digital.
            </Alert>
          ) : null}

          {!hasApprovedProject && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: 2 }}>
              Para submeter uma monografia, você precisa primeiro ter um projeto aprovado.
            </Alert>
          )}

          {loadingMonografias ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : errorMonografias ? (
            <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
              {errorMonografias}
              <Button 
                color="inherit" 
                size="small" 
                onClick={carregarMonografias} 
                sx={{ ml: 2, minHeight: 32 }}
              >
                Tentar Novamente
              </Button>
            </Alert>
          ) : monografias.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              {monografias.map((monografia) => {
                const statusColor = getStatusColor(monografia.status);
                const statusDisplayName = getStatusDisplayName(monografia.status);
                const availableActions = getAvailableActions('estudante', monografia.status);
                
                return (
                  <ExpandableCard
                    key={monografia.id}
                    title={monografia.projeto?.proposta?.titulo || monografia.titulo || 'Título não disponível'}
                    subtitle={`Versão: ${monografia.versao_atual || 'v1.0'}`}
                    expanded={!isMobile}
                    statusChip={
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Chip
                          label={statusDisplayName}
                          color={statusColor}
                          size="small"
                          sx={{ fontWeight: 'bold' }}
                        />
                        {monografia.numero_resubmissoes > 0 && (
                          <Chip 
                            variant="outlined"
                            size="small"
                            color="warning"
                            label={`${monografia.numero_resubmissoes} resubmissão${monografia.numero_resubmissoes > 1 ? 'ões' : ''}`}
                          />
                        )}
                      </Box>
                    }
                    actions={[
                      ...(monografia.documento_monografia ? [
                        <Button
                          key="view"
                          variant="outlined"
                          size={isMobile ? "medium" : "small"}
                          startIcon={<VisibilityIcon />}
                          onClick={() => handleViewDocument(monografia, 'monografia')}
                          sx={{ minHeight: 40 }}
                        >
                          Ver Doc
                        </Button>,
                        <Button
                          key="download"
                          variant="outlined"
                         size={isMobile ? "medium" : "small"}
                          startIcon={<DownloadIcon />}
                          onClick={() => handleDownloadDocument(monografia, 'monografia')}
                          sx={{ minHeight: 40 }}
                        >
                          Download
                        </Button>
                      ] : []),
                      <Button
                        key="details"
                        variant="text"
                        size={isMobile ? "medium" : "small"}
                        endIcon={<ChevronRightIcon />}
                        onClick={() => navigate(`/monografias/${monografia.id}`)}
                        sx={{ 
                          flex: isMobile ? 1 : 'none',
                          minHeight: 40
                        }}
                      >
                        Detalhes
                      </Button>,
                      <Button
                        key="chat"
                        variant="outlined"
                        size={isMobile ? "medium" : "small"}
                        startIcon={<ChatIcon />}
                        onClick={() => openChatDialogHandler(monografia, 'monografia')}
                        sx={{ minHeight: 40 }}
                      >
                        Chat
                      </Button>,
                      <Button
                        key="historico"
                        variant="outlined"
                        size={isMobile ? "medium" : "small"}
                        color="info"
                        startIcon={<EventIcon />}
                        onClick={() => handleAbrirHistorico(monografia)}
                        sx={{ minHeight: 40 }}
                      >
                        Histórico
                      </Button>,
                      ...(monografia.numero_resubmissoes > 0 ? [
                        <Button
                          key="versoes"
                          variant="outlined"
                          size={isMobile ? "medium" : "small"}
                          color="secondary"
                          startIcon={<DescriptionIcon />}
                          onClick={() => handleAbrirVersoes(monografia)}
                          sx={{ minHeight: 40 }}
                        >
                          Versões
                        </Button>
                      ] : []),
                      ...(temDocumentoCorrigido(monografia.observacoes_supervisor) ? [
                        <Button
                          key="doc-corrigido"
                          variant="outlined"
                          size={isMobile ? "medium" : "small"}
                          color="secondary"
                          startIcon={<DownloadIcon />}
                          onClick={() => downloadDocumentoCorrigido(monografia.id)}
                          disabled={loadingDownload}
                          sx={{ minHeight: 40 }}
                        >
                          {loadingDownload ? 'Baixando...' : 'Doc. Corrigido'}
                        </Button>
                      ] : []),
                      ...availableActions.map((action) => {
                        if (action.key === 'resubmeter') {
                          return (
                            <Button
                              key={action.key}
                              variant="contained"
                              color="warning"
                              size={isMobile ? "medium" : "small"}
                              startIcon={<UploadIcon />}
                              onClick={() => handleResubmeterMonografia(monografia)}
                              disabled={submittingAction}
                              sx={{ minHeight: 40 }}
                            >
                              Resubmeter
                            </Button>
                          );
                        }
                        
                        if (action.key === 'submeter-direcao') {
                          return (
                            <Button
                              key={action.key}
                              variant="contained"
                              color="primary"
                              size={isMobile ? "medium" : "small"}
                              startIcon={<SendIcon />}
                              onClick={() => handleSubmeterParaDirecao(monografia)}
                              disabled={submittingAction}
                              sx={{ minHeight: 40 }}
                            >
                              Submeter à Direção
                            </Button>
                          );
                        }
                        
                        return null;
                      })
                    ]}
                  >
                    <Box sx={{ mb: 2 }}>
                      {monografia.data_submissao && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Submetida em: {new Date(monografia.data_submissao).toLocaleDateString()}
                        </Typography>
                      )}

                      {monografia.data_resubmissao && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Resubmetida em: {new Date(monografia.data_resubmissao).toLocaleDateString()}
                        </Typography>
                      )}

                      {monografia.data_aprovacao_supervisor && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                          Aprovada pelo supervisor em: {new Date(monografia.data_aprovacao_supervisor).toLocaleDateString()}
                        </Typography>
                      )}
                    </Box>

                    {monografia.observacoes_supervisor && (
                      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2">Observações do Supervisor:</Typography>
                        <Typography variant="body2">{monografia.observacoes_supervisor}</Typography>
                      </Alert>
                    )}

                    {monografia.observacoes_aprovacao_supervisor && (
                      <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2">Observações da Aprovação:</Typography>
                        <Typography variant="body2">{monografia.observacoes_aprovacao_supervisor}</Typography>
                      </Alert>
                    )}

                    {monografia.observacoes_devolucao_direcao && (
                      <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                        <Typography variant="subtitle2">Devolvida pela Direção Científica:</Typography>
                        <Typography variant="body2">{monografia.observacoes_devolucao_direcao}</Typography>
                        {monografia.tipo_devolucao_direcao && (
                          <Typography variant="caption" sx={{ mt: 1, display: 'block' }}>
                            Tipo: {monografia.tipo_devolucao_direcao === 'FORMAL' ? 'Problemas Formais' : 'Problemas de Conteúdo'}
                          </Typography>
                        )}
                      </Alert>
                    )}

                    {monografia.prazo_correcoes && monografia.status === 'CORRECOES_SOLICITADAS' && (
                      <Alert 
                        severity={new Date(monografia.prazo_correcoes) < new Date() ? 'error' : 'warning'} 
                        sx={{ mb: 2, borderRadius: 2 }}
                      >
                        <Typography variant="subtitle2">Prazo para Correções:</Typography>
                        <Typography variant="body2">
                          {new Date(monografia.prazo_correcoes).toLocaleDateString()}
                          {new Date(monografia.prazo_correcoes) < new Date() && ' (VENCIDO)'}
                        </Typography>
                      </Alert>
                    )}

                    {monografia.status === 'CORRECOES_SOLICITADAS' && (
                      <SecaoCorrecoesSolicitadas monografia={monografia} />
                    )}
                  </ExpandableCard>
                );
              })}
            </Box>
          ) : (
            <Alert severity="info" icon={<DescriptionIcon />} sx={{ borderRadius: 2 }}>
              {hasApprovedProject
                ? 'Você tem projetos aprovados. Submeta sua monografia!'
                : 'Você ainda não tem monografias submetidas. Aguarde a aprovação do seu projeto.'}
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderDefesas = () => {
    return (
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Minhas Defesas
          </Typography>

          {loadingDefesas ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : errorDefesas ? (
            <Alert 
              severity="error" 
              sx={{ mb: 2, borderRadius: 2 }}
              action={
                <Button color="inherit" size="small" onClick={() => carregarDefesas()}>
                  Tentar Novamente
                </Button>
              }
            >
              {errorDefesas}
            </Alert>
          ) : defesas && defesas.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              {defesas.map((defesa) => (
                <ExpandableCard
                  key={defesa.id}
                  title={defesa?.monografia?.projeto?.proposta?.titulo || 'Título não disponível'}
                  subtitle={`Data: ${defesa?.data_defesa ? new Date(defesa.data_defesa).toLocaleDateString() : 'Data não definida'}`}
                  expanded={!isMobile}
                  statusChip={
                    <Chip
                      label={defesa?.status || 'Status não definido'}
                      color={
                        defesa?.status === 'REALIZADA' ? 'success' :
                        defesa?.status === 'CANCELADA' ? 'error' : 'warning'
                      }
                      size="small"
                    />
                  }
                  actions={[
                    <Button
                      key="detalhes"
                      variant="outlined"
                      size={isMobile ? "medium" : "small"}
                      endIcon={<ChevronRightIcon />}
                      onClick={() => navigate(`/defesas/${defesa.id}`)}
                      sx={{ 
                        flex: isMobile ? 1 : 'none',
                        minHeight: 40
                      }}
                    >
                      Ver Detalhes
                    </Button>
                  ]}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Local: {defesa?.local_defesa || 'Local não definido'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Presidente: {defesa?.presidente?.nome || 'Não definido'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Arguente: {defesa?.arguente?.nome || 'Não definido'}
                    </Typography>
                  </Box>
                </ExpandableCard>
              ))}
            </Box>
          ) : (
            <Alert severity="info" icon={<EventIcon />} sx={{ borderRadius: 2 }}>
              {monografias?.some(m => m.status === 'SUBMETIDA')
                ? 'Sua defesa ainda não foi agendada. Aguarde o agendamento.'
                : 'Você ainda não tem defesas agendadas. Submeta sua monografia primeiro.'}
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderAvaliacoes = () => {
    return (
      <Card sx={{ borderRadius: 2 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Minhas Avaliações
          </Typography>

          {loadingAvaliacoes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : errorAvaliacoes ? (
            <Alert 
              severity="error" 
              sx={{ mb: 2, borderRadius: 2 }}
              action={
                <Button color="inherit" size="small" onClick={carregarAvaliacoes}>
                  Tentar Novamente
                </Button>
              }
            >
              {errorAvaliacoes}
            </Alert>
          ) : avaliacoes.length > 0 ? (
            <Box sx={{ mt: 2 }}>
              {avaliacoes.map((avaliacao) => (
                <ExpandableCard
                  key={avaliacao.id}
                  title={avaliacao.defesa?.monografia?.projeto?.proposta?.titulo || 'Título não disponível'}
                  subtitle={`Data da Defesa: ${avaliacao.dataDefesa ? new Date(avaliacao.dataDefesa).toLocaleDateString() : 'N/A'}`}
                  expanded={!isMobile}
                  statusChip={
                    <Chip
                      label={`Média: ${avaliacao.mediaFinal || 'N/A'}`}
                      color="primary"
                      size="small"
                    />
                  }
                  actions={[
                    <Button
                      key="detalhes"
                      variant="outlined"
                      size={isMobile ? "medium" : "small"}
                      endIcon={<ChevronRightIcon />}
                      onClick={() => navigate(`/avaliacoes/${avaliacao.id}`)}
                      sx={{ 
                        flex: isMobile ? 1 : 'none',
                        minHeight: 40
                      }}
                    >
                      Ver Detalhes Completos
                    </Button>
                  ]}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant="subtitle2" color="primary">
                      Notas:
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                      <Typography variant="body2">
                        Trabalho Escrito: <strong>{avaliacao.notaTrabalhoEscrito || 'N/A'}</strong>
                      </Typography>
                      <Typography variant="body2">
                        Defesa Oral: <strong>{avaliacao.notaDefesa || 'N/A'}</strong>
                      </Typography>
                      <Typography variant="body2" color="primary">
                        Média Final: <strong>{avaliacao.mediaFinal || 'N/A'}</strong>
                      </Typography>
                    </Box>
                  </Box>
                </ExpandableCard>
              ))}
            </Box>
          ) : (
            <Alert severity="info" icon={<AssessmentIcon />} sx={{ borderRadius: 2 }}>
              {defesas.some(d => d?.status === 'REALIZADA')
                ? 'Sua avaliação ainda não foi registrada. Aguarde o registro.'
                : 'Você ainda não tem avaliações. Realize sua defesa primeiro.'}
            </Alert>
          )}
        </CardContent>
      </Card>
    );
  };

  const renderDrawer = () => {
    const drawerItems = availableTabs;
    
    const drawer = (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{
          p: isMobile ? 2 : 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          background: 'linear-gradient(135deg, #1976d2 0%, #64b5f6 100%)',
          color: 'white',
        }}>
          <Avatar 
            sx={{ 
              width: isMobile ? 60 : 80, 
              height: isMobile ? 60 : 80,
              mb: 2,
              bgcolor: 'white',
              color: 'primary.main',
              border: '3px solid white',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
            }}
            src={user?.avatarUrl}
          >
            {!user?.avatarUrl && user?.nome?.charAt(0)}
          </Avatar>
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            sx={{ 
              fontWeight: 600,
              textAlign: 'center',
              wordBreak: 'break-word'
            }}
          >
            {user?.nome}
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ opacity: 0.9, textAlign: 'center' }}
          >
            Estudante
          </Typography>
        </Box>

        <List sx={{ px: 1, py: 1, flex: 1, overflow: 'auto' }}>
          {drawerItems.map((item) => (
            <ListItem key={item.label} disablePadding sx={{ mb: 0.5 }}>
              <ListItemButton
                onClick={() => {
                  setValue(item.value);
                  if (isMobile) setDrawerOpen(false);
                }}
                selected={value === item.value}
                sx={{
                  borderRadius: 2,
                  minHeight: 48,
                  px: 2,
                  transition: 'all 0.3s',
                  '&.Mui-selected': {
                    bgcolor: 'primary.main',
                    color: 'white',
                    '&:hover': { bgcolor: 'primary.dark' },
                    '& .MuiListItemIcon-root': { color: 'white' },
                  },
                  '&:hover': { 
                    bgcolor: 'rgba(25, 118, 210, 0.08)',
                    transform: 'translateX(4px)'
                  },
                }}
              >
                <ListItemIcon sx={{ 
                  minWidth: 40, 
                  color: value === item.value ? 'white' : 'primary.main',
                  transition: 'color 0.3s'
                }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.label}
                  primaryTypographyProps={{ 
                    fontSize: isMobile ? '0.875rem' : '0.95rem',
                    fontWeight: value === item.value ? 600 : 400 
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Box sx={{ 
          p: 2, 
          borderTop: '1px solid rgba(0,0,0,0.08)', 
          display: 'flex', 
          flexDirection: 'column', 
          gap: 1 
        }}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => {
              handleEditProfile();
              if (isMobile) setDrawerOpen(false);
            }}
            fullWidth
            size={isMobile ? "medium" : "small"}
            sx={{ 
              borderRadius: 2, 
              textTransform: 'none',
              minHeight: 40
            }}
          >
            Editar Perfil
          </Button>
          <Button
            variant="contained"
            startIcon={<LogoutIcon />}
            onClick={() => {
              handleLogout();
              if (isMobile) setDrawerOpen(false);
            }}
            fullWidth
            size={isMobile ? "medium" : "small"}
            sx={{ 
              borderRadius: 2,
              bgcolor: 'error.main',
              '&:hover': { bgcolor: 'error.dark' },
              textTransform: 'none',
              minHeight: 40
            }}
          >
            Sair
          </Button>
        </Box>
      </Box>
    );

    if (isMobile) {
      return (
        <SwipeableDrawer
          anchor="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onOpen={() => setDrawerOpen(true)}
          disableSwipeToOpen={false}
          sx={{
            '& .MuiDrawer-paper': {
              width: Math.min(280, window.innerWidth * 0.8),
              boxSizing: 'border-box',
              background: '#ffffff',
            },
          }}
        >
          {drawer}
        </SwipeableDrawer>
      );
    }

    return (
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={handleDrawerToggle}
        sx={{
          width: 280,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: 280,
            boxSizing: 'border-box',
            background: '#ffffff',
          },
        }}
      >
        {drawer}
      </Drawer>
    );
  };

  // Componente para loading responsivo
  const ResponsiveLoading = ({ message = "Carregando..." }) => (
    <Box sx={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center', 
      minHeight: isMobile ? '50vh' : '60vh',
      p: 3
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        gap: 2,
        textAlign: 'center'
      }}>
        <CircularProgress size={isMobile ? 40 : 50} />
        <Typography 
          variant={isMobile ? "body1" : "h6"} 
          color="text.secondary"
        >
          {message}
        </Typography>
      </Box>
    </Box>
  );

  ResponsiveLoading.propTypes = {
    message: PropTypes.string
  };

  if (loadingTabs) {
    return <ResponsiveLoading message="Carregando dashboard do estudante..." />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {console.log('neverShowModal:', neverShowModal, 'showRecomendacoesModal:', showRecomendacoesModal)}
      {console.log('showRecomendacoesAlert:', showRecomendacoesAlert)}
      
      {!neverShowModal && (
        <Modal 
          open={showRecomendacoesModal} 
          onClose={handleShowLater} 
          closeAfterTransition
        >
          <Fade in={showRecomendacoesModal}>
            <Paper sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: '95%', sm: '90%', md: 600 },
              maxWidth: '600px',
              p: { xs: 3, sm: 4 },
              borderRadius: 3,
              boxShadow: 24,
              textAlign: 'center',
              maxHeight: '90vh',
              overflow: 'auto'
            }}>
              <Box sx={{ mb: 3 }}>
                <RecommendIcon sx={{ 
                  fontSize: { xs: 48, sm: 60 }, 
                  color: 'primary.main' 
                }} />
              </Box>
              <Typography 
                variant={isMobile ? "h6" : "h5"} 
                sx={{ mb: 2, fontWeight: 'bold' }}
              >
                Recomendações Acadêmicas Personalizadas
              </Typography>
              <Typography 
                variant="body1" 
                sx={{ 
                  mb: 3,
                  fontSize: isMobile ? '0.875rem' : '1rem',
                  lineHeight: 1.6
                }}
              >
                Antes de efetuar sua pré-inscrição, confira a área de recomendações personalizadas! 
                Nosso sistema vai te ajudar a encontrar as melhores opções educacionais de acordo 
                com seu histórico acadêmico e local de residência.
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                gap: 1, 
                justifyContent: 'center',
                flexDirection: isMobile ? 'column' : 'row'
              }}>
                <Button 
                  variant="contained" 
                  onClick={handleVerRecomendacoes}
                  startIcon={<RecommendIcon />}
                  sx={{ minHeight: 40 }}
                >
                  Ver Recomendações
                </Button>
                <Button 
                  variant="outlined" 
                  onClick={handleShowLater}
                  sx={{ minHeight: 40 }}
                >
                  Ver Depois
                </Button>
                <Button 
                  variant="text" 
                  onClick={handleNeverShowAgain}
                  size="small"
                  sx={{ minHeight: 40 }}
                >
                  Não Mostrar Mais
                </Button>
              </Box>
            </Paper>
          </Fade>
        </Modal>
      )}

      {showRecomendacoesAlert && (
        <Alert
          severity="info"
          variant="filled"
          sx={{
            position: 'fixed',
            bottom: { xs: 16, sm: 24 },
            right: { xs: 16, sm: 24 },
            left: { xs: 16, sm: 'auto' },
            zIndex: 1000,
            maxWidth: { xs: 'none', sm: 400 },
            boxShadow: 4,
            borderRadius: 2
          }}
          action={
            <Box sx={{ 
              display: 'flex', 
              gap: 1,
              flexDirection: isMobile ? 'column' : 'row',
              alignItems: 'stretch'
            }}>
              <Button 
                color="inherit" 
                size="small" 
                onClick={handleVerRecomendacoes}
                sx={{ minHeight: 32 }}
              >
                Ver Agora
              </Button>
              <IconButton 
                size="small" 
                color="inherit" 
                onClick={() => setShowRecomendacoesAlert(false)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          }
        >
          <AlertTitle sx={{ fontSize: isMobile ? '0.875rem' : '1rem' }}>
            {recomendacoes.some(rec => rec.pontuacaoTotal >= 70)
              ? "Encontramos cursos ideais para você!"
              : "Encontre as instituições perfeitas para sua formação!"}
          </AlertTitle>
          <Typography sx={{ fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
            {recomendacoes.some(rec => rec.pontuacaoTotal >= 70)
              ? "Temos recomendações que combinam com seu perfil!"
              : "Acesse nossas recomendações personalizadas."}
          </Typography>
        </Alert>
      )}

      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          height: { xs: 64, sm: 72 }
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 64, sm: 72 }, px: { xs: 1, sm: 3 } }}>
          <IconButton 
            size="large" 
            edge="start" 
            color="inherit" 
            onClick={handleDrawerToggle} 
            sx={{ 
              mr: { xs: 1, sm: 2 },
              minHeight: 40,
              minWidth: 40
            }}
          >
            <MenuIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
          
          <Typography 
            variant={isMobile ? "subtitle1" : "h6"} 
            component="div" 
            sx={{ 
              flexGrow: 1,
              fontSize: { xs: '1rem', sm: '1.25rem' },
              fontWeight: 600
            }}
          >
            {isMobile ? "Dashboard" : "Dashboard do Estudante"}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 0.5, sm: 2 } }}>
            {renderNotificationsIcon()}
            <Button 
              color="inherit" 
              onClick={handleMenu} 
              startIcon={<AccountCircle sx={{ fontSize: { xs: 18, sm: 24 } }} />}
              sx={{ 
                textTransform: 'none',
                minHeight: 40,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                px: { xs: 1, sm: 2 }
              }}
            >
              {isMobile ? user?.nome?.split(' ')[0] : user?.nome}
            </Button>
          </Box>
        </Toolbar>
      </AppBar>

      {renderDrawer()}

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem 
          onClick={handleEditProfile}
          sx={{ minHeight: 48, px: 2 }}
        >
          <EditIcon fontSize="small" sx={{ mr: 2 }} />
          Editar Perfil
        </MenuItem>
        <MenuItem 
          onClick={handleLogout}
          sx={{ minHeight: 48, px: 2 }}
        >
          <LogoutIcon fontSize="small" sx={{ mr: 2 }} />
          Sair
        </MenuItem>
      </Menu>

      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          p: { xs: 1, sm: 2, md: 3 }, 
          mt: { xs: 8, sm: 9 },
          pb: { xs: 10, sm: 4 }
        }}
      >
        <Box sx={{ 
          borderRadius: { xs: 1, sm: 2 }, 
          boxShadow: 1, 
          backgroundColor: 'background.paper', 
          padding: { xs: '4px', sm: '8px' }, 
        mb: { xs: 2, sm: 3 },
          overflow: 'hidden'
        }}>
          {availableTabs.length > 0 ? (
            <Tabs
              value={value}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ 
                '& .MuiTabs-indicator': { 
                  height: '3px', 
                  borderRadius: '2px', 
                  backgroundColor: 'primary.main' 
                },
                '& .MuiTab-root': {
                  minHeight: { xs: 48, sm: 56 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  textTransform: 'none',
                  fontWeight: 500
                },
                '& .MuiTabs-scrollButtons': {
                  '&.Mui-disabled': { opacity: 0.3 }
                }
              }}
            >
              {availableTabs.map(tab => (
                <Tab 
                  key={tab.value}
                  icon={React.cloneElement(tab.icon, { 
                    sx: { fontSize: { xs: 18, sm: 20 } } 
                  })}
                  label={tab.label} 
                  value={tab.value}
                  sx={{ 
                    minWidth: { xs: 80, sm: 120 },
                    px: { xs: 1, sm: 2 },
                    borderRadius: 1,
                    margin: '0 2px',
                    transition: 'all 0.3s',
                    '&.Mui-selected': { 
                      backgroundColor: 'primary.light', 
                      color: 'primary.contrastText',
                      fontWeight: 600
                    }, 
                    '&:hover': { 
                      backgroundColor: 'primary.light',
                      opacity: 0.8
                    }
                  }} 
                />
              ))}
            </Tabs>
          ) : (
            <Alert severity="info" sx={{ m: 1, borderRadius: 2 }}>
              Não há módulos disponíveis para exibição. Entre em contato com a administração do sistema.
            </Alert>
          )}
        </Box>

        {/* Renderizar conteúdo das abas */}
        <TabPanel value={value} index={0}>{renderCampanhas()}</TabPanel>
        <TabPanel value={value} index={1}>{renderPreInscricoes()}</TabPanel>
        <TabPanel value={value} index={2}>{renderProximosPrazos()}</TabPanel>
        <TabPanel value={value} index={3}>{renderPagamentos()}</TabPanel>
        <TabPanel value={value} index={4}>
          <Box sx={{ maxWidth: '100%', mx: 'auto' }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2,
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 1 : 0
            }}>
              <Typography variant={isMobile ? "h6" : "h5"}>
                Notificações
              </Typography>
              <IconButton 
                onClick={handleRefreshNotificacoes} 
                title="Atualizar notificações"
                sx={{ minHeight: 40, minWidth: 40 }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
            {notificacoesLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <CentralNotificacoes />
            )}
          </Box>
        </TabPanel>
        <TabPanel value={value} index={5}>{renderRecomendacoes()}</TabPanel>
        <TabPanel value={value} index={6}>
          <Box sx={{ mb: 3 }}>
            <Tabs 
              value={tccTab} 
              onChange={(_, newValue) => setTccTab(newValue)} 
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              sx={{ 
                borderBottom: 1, 
                borderColor: 'divider',
                '& .MuiTab-root': {
                  minHeight: { xs: 48, sm: 56 },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  textTransform: 'none',
                  fontWeight: 500,
                  minWidth: { xs: 100, sm: 140 }
                }
              }}
            >
              <Tab 
                icon={<ArticleIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />} 
                label="Propostas" 
                iconPosition="start" 
              />
              <Tab 
                icon={<AssignmentIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />} 
                label="Projetos" 
                iconPosition="start" 
              />
              <Tab 
                icon={<DescriptionIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />} 
                label="Monografias" 
                iconPosition="start" 
              />
              <Tab 
                icon={<EventIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />} 
                label="Defesas" 
                iconPosition="start" 
              />
              <Tab 
                icon={<AssessmentIcon sx={{ fontSize: { xs: 16, sm: 20 } }} />} 
                label="Avaliações" 
                iconPosition="start" 
              />
            </Tabs>
          </Box>

          {tccTab === 0 && renderPropostasTCC()}
          {tccTab === 1 && renderProjetosTCC()}
          {tccTab === 2 && renderMonografias()}
          {tccTab === 3 && renderDefesas()}
          {tccTab === 4 && renderAvaliacoes()}
        </TabPanel>
      </Box>

      {/* Backdrop para loading de operações */}
      <Backdrop
        sx={{ 
          color: '#fff', 
          zIndex: (theme) => theme.zIndex.drawer + 1,
          flexDirection: 'column',
          gap: 2
        }}
        open={submittingAction}
      >
        <CircularProgress color="inherit" size={isMobile ? 40 : 60} />
        <Typography variant={isMobile ? "body1" : "h6"} sx={{ textAlign: 'center' }}>
          Processando solicitação...
        </Typography>
        <Typography 
          variant="body2" 
          sx={{ 
            textAlign: 'center', 
            maxWidth: isMobile ? 250 : 300,
            px: 2
          }}
        >
          Por favor, aguarde enquanto processamos sua solicitação.
        </Typography>
      </Backdrop>

      {/* Sistema de notificações responsivo */}
      <NotificationSnackbar
        open={notification.open}
        message={notification.message}
        severity={notification.severity}
        onClose={handleCloseNotification}
      />

      {/* Diálogo de histórico completo responsivo */}
      <Dialog
        open={historicoDialogOpen}
        onClose={() => setHistoricoDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            margin: isMobile ? 0 : 2,
            borderRadius: isMobile ? 0 : 2
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          pb: 1
        }}>
          <Typography variant={isMobile ? "h6" : "h5"}>
            Histórico Completo da Monografia
          </Typography>
          {isMobile && (
            <IconButton 
              onClick={() => setHistoricoDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 1, sm: 3 } }}>
          {historicoData && (
            <Box>
              <Box sx={{ 
                mb: 3, 
                p: { xs: 1.5, sm: 2 }, 
                bgcolor: 'grey.50', 
                borderRadius: 1 
              }}>
                <Typography variant={isMobile ? "subtitle1" : "h6"}>
                  {historicoData.monografia.titulo}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Status atual: {getStatusDisplayName(historicoData.monografia.status)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Versão atual: {historicoData.monografia.versao_atual}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total de resubmissões: {historicoData.monografia.numero_resubmissoes}
                </Typography>
              </Box>

              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom>
                Timeline de Ações
              </Typography>
              {historicoData.timeline && historicoData.timeline.length > 0 ? (
                historicoData.timeline.map((item, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      mb: 2, 
                      p: { xs: 1.5, sm: 2 }, 
                      border: 1, 
                      borderColor: 'grey.300', 
                      borderRadius: 1,
                      position: 'relative'
                    }}
                  >
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'flex-start', 
                      mb: 1,
                      flexDirection: isMobile ? 'column' : 'row',
                      gap: isMobile ? 1 : 0
                    }}>
                      <Typography 
                        variant="subtitle2" 
                        sx={{ 
                          fontWeight: 'bold',
                          fontSize: { xs: '0.875rem', sm: '1rem' }
                        }}
                      >
                        {item.acao.replace(/_/g, ' ')}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.8125rem' } }}
                      >
                        {new Date(item.data).toLocaleString('pt-BR')}
                      </Typography>
                    </Box>
                    
                    <Typography 
                      variant="caption" 
                      color="primary.main" 
                      sx={{ display: 'block', mb: 1 }}
                    >
                      Versão: {item.versao}
                    </Typography>
                    
                    {item.dados.observacoes && (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          mt: 1, 
                          p: 1, 
                          bgcolor: 'grey.100', 
                          borderRadius: 1,
                          fontStyle: 'italic',
                          fontSize: { xs: '0.8125rem', sm: '0.875rem' },
                          lineHeight: 1.4
                        }}
                      >
                        {item.dados.observacoes}
                      </Typography>
                    )}
                    
                    {item.dados.prazo && (
                      <Typography 
                        variant="caption" 
                        color="warning.main" 
                        sx={{ display: 'block', mt: 1 }}
                      >
                        Prazo: {new Date(item.dados.prazo).toLocaleDateString('pt-BR')}
                      </Typography>
                    )}
                  </Box>
                ))
              ) : (
                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  Nenhuma ação registrada no histórico.
                </Alert>
              )}

              {historicoData.estatisticas && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>Estatísticas</Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={4}>
                      <Typography variant="body2">
                        <strong>Total de ações:</strong> {historicoData.estatisticas.total_acoes}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2">
                        <strong>Resubmissões:</strong> {historicoData.estatisticas.numero_resubmissoes}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="body2">
                        <strong>Tempo total:</strong> {
                          historicoData.estatisticas.tempo_total ? 
                          `${Math.ceil(historicoData.estatisticas.tempo_total / (1000 * 60 * 60 * 24))} dias` : 
                          'N/A'
                        }
                      </Typography>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button 
            onClick={() => setHistoricoDialogOpen(false)}
            variant={isMobile ? "contained" : "outlined"}
            fullWidth={isMobile}
            sx={{ minHeight: 40 }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo de versões de documentos */}
      <Dialog
        open={versaoDialogOpen}
        onClose={() => setVersaoDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        sx={{
          '& .MuiDialog-paper': {
            margin: isMobile ? 0 : 2,
            borderRadius: isMobile ? 0 : 2
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <Typography variant={isMobile ? "h6" : "h5"}>
            Versões de Documentos
          </Typography>
          {isMobile && (
            <IconButton 
              onClick={() => setVersaoDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          )}
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 1, sm: 3 } }}>
          {versaoData && versaoData.length > 0 ? (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Histórico de todas as versões submetidas desta monografia:
              </Typography>
              
              {versaoData.map((versao, index) => (
                <Box 
                  key={index}
                  sx={{ 
                    mb: 2, 
                    p: { xs: 1.5, sm: 2 }, 
                    border: 1, 
                    borderColor: versao.acao === 'SUBMISSAO_INICIAL' ? 'success.main' : 'warning.main',
                    borderRadius: 1,
                    bgcolor: versao.acao === 'SUBMISSAO_INICIAL' ? 'success.50' : 'warning.50'
                  }}
                >
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center', 
                    mb: 1,
                    flexDirection: isMobile ? 'column' : 'row',
                    alignItems: isMobile ? 'flex-start' : 'center',
                    gap: isMobile ? 1 : 0
                  }}>
                    <Typography variant="subtitle2">
                      {versao.acao === 'SUBMISSAO_INICIAL' ? 'Submissão Inicial' : 'Resubmissão'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {new Date(versao.data).toLocaleString('pt-BR')}
                    </Typography>
                  </Box>
                  
                  <Typography variant="body2" color="primary.main">
                    Versão: {versao.versao}
                  </Typography>
                  
                  {versao.numero_resubmissao > 0 && (
                    <Typography variant="caption" color="warning.main">
                      Resubmissão #{versao.numero_resubmissao}
                    </Typography>
                  )}
                  
                  {versao.documento && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                      Arquivo: {versao.documento}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          ) : (
            <Alert severity="info">Nenhuma versão encontrada.</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: { xs: 2, sm: 3 } }}>
          <Button 
            onClick={() => setVersaoDialogOpen(false)}
            variant={isMobile ? "contained" : "outlined"}
            fullWidth={isMobile}
            sx={{ minHeight: 40 }}
          >
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* ChatDialog responsivo */}
      <ChatDialog
        open={chatDialogOpen}
        onClose={() => {
          setChatDialogOpen(false);
          setSelectedChatItem(null);
          setChatMessage('');
          setChatHistory([]);
          showNotification('Chat fechado.', 'info');
        }}
        context={selectedChatItem?.context || 'projeto'}
        contextId={selectedChatItem?.data?.id || 0}
        currentUser={user?.nome}
        title={
          selectedChatItem?.data?.nome
            ? `Chat com ${selectedChatItem.data.nome}`
            : `Chat - ${selectedChatItem?.context || 'Conversa'}`
        }
        messages={chatHistory}
        loading={loadingChat}
        onSendMessage={sendChatMessageHandler}
        message={chatMessage}
        onMessageChange={(e) => setChatMessage(e.target.value)}
        onRefresh={async () => {
          if (selectedChatItem) {
            setLoadingChat(true);
            showNotification('Atualizando chat...', 'info');
            try {
              const response = await api.get('/chat/messages', {
                params: { 
                  context: selectedChatItem.context, 
                  context_id: selectedChatItem.data.id 
                }
              });
              setChatHistory(response.data);
              showNotification('Chat atualizado!', 'success');
            } catch (error) {
              console.error("Erro ao atualizar chat:", error);
              handleApiError(error, 'atualização do chat');
            } finally {
              setLoadingChat(false);
            }
          }
        }}
        showNotification={showNotification}
        fullScreen={isMobile}
      />

      {/* Footer responsivo */}
      <Box sx={{ 
        mt: 'auto', 
        position: isMobile ? 'relative' : 'static',
        zIndex: 1
      }}>
        <Footer />
      </Box>
    </Box>
  );
};

export default EstudanteDashboardPage;